import { QueryClient, UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { DATA_TYPE_TO_EVENT_VERSION } from "components/saved_searches/utils";
import toast from "components/ui/toast/Toast";
import { graphql } from "lib/generated/app-service-gql";
import {
  UpdateSavedViewMutation,
  UpdateSavedViewMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { createSavedViewUpdatedEvent } from "lib/savedViewTracking";
import { EventNames, useTracking } from "lib/tracking";
import { useGraphQlMutation } from "../useGraphQlClient";

const updateSavedView = graphql(`
  mutation UpdateSavedView($request: UpdateSavedViewRequest!) {
    updateSavedView(UpdateSavedViewRequest: $request) {
      id
      name
      description
      updatedAt
      dataType
    }
  }
`);

/**
 * Refetches the caches the view lives in, reporting whether they actually came back. `throwOnError`
 * is what makes that observable — react-query swallows refetch failures by default.
 */
async function refetchViewCaches(queryClient: QueryClient): Promise<boolean> {
  try {
    await Promise.all([
      queryClient.refetchQueries({ queryKey: ["views"] }, { throwOnError: true }),
      queryClient.refetchQueries({ queryKey: ["savedViews"] }, { throwOnError: true }),
    ]);
    return true;
  } catch {
    return false;
  }
}

export function useUpdateSavedView(
  contextSource: string,
  options?: UseMutationOptions<
    UpdateSavedViewMutation,
    unknown,
    UpdateSavedViewMutationVariables,
    unknown
  >,
) {
  const queryClient = useQueryClient();
  const { logEvent } = useTracking();

  const { data, ...rest } = useGraphQlMutation(updateSavedView, {
    ...options,
    onSuccess: async (data, variables, context) => {
      // Await refetch so the updated view is in the cache before the consumer's onSuccess
      // runs. Without this, the stale cached view briefly causes hasUserModifiedFilters to
      // be true (old view filters ≠ current URL filters), creating a false "unsaved" flash.
      const refreshed = await refetchViewCaches(queryClient);

      void queryClient.invalidateQueries({ queryKey: ["leadSubscriptionContents"] });
      void queryClient.invalidateQueries({ queryKey: ["leadSubscriptions"] });

      // The update itself landed either way, so it is tracked either way.
      logEvent(
        EventNames.viewUpdated,
        createSavedViewUpdatedEvent(
          variables.request.name ?? data.updateSavedView.name,
          DATA_TYPE_TO_EVENT_VERSION[data.updateSavedView.dataType],
          contextSource,
          variables.request.id,
          undefined,
          !variables.request.alertFrequency ? "NEVER" : variables.request.alertFrequency,
        ),
      );

      if (!refreshed) {
        // The write landed but the cache still holds the pre-save view. The consumer's onSuccess
        // clears the URL filters, which would render that stale copy back over what the user just
        // saved — and saving again from there writes the old values in for real. So stop here and
        // leave their filters on screen.
        toast.error("Saved, but the page couldn't be refreshed. Reload to see the latest version.");
        return;
      }

      options?.onSuccess && options?.onSuccess(data, variables, context);
    },
  });

  return { data: data?.updateSavedView, ...rest };
}
